import { useMutation } from "@tanstack/react-query"
import { ArrowDownToLine, RefreshCw, Sparkles } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { api } from "@/api/cliente"
import { monto } from "@/componentes/tesoreria/dinero"
import type { Adjuntando } from "@/componentes/ui/AdjuntosNuevos"
import { AdjuntosNuevos, archivosPegados, sumar } from "@/componentes/ui/AdjuntosNuevos"
import { Boton } from "@/componentes/ui/Boton"
import { paraElServidor as aDecimal } from "@/lib/decimales"
import { comoFecha } from "@/lib/fechas"
import type { BorradorRetencion, Calculo, LineaRetencion } from "./tipos"

type Cuadre = {
  lineas: (Omit<LineaRetencion, "monto_usd"> & { monto_usd?: string | null })[]
  calculo: Calculo
  tasa: string | null
  origen_tasa: "documento" | "tecleada" | "pago" | null
  diferencia: string | null
}

type RenglonUsd = {
  descripcion: string
  monto_usd: string
  exento_iva: boolean
  retiene_iva: boolean
  retiene_islr: boolean
}

type Pago = {
  monto: string | null
  moneda: string | null
  fecha: string | null
  referencia: string | null
  beneficiario: string | null
  estado: string
}

type Lectura = {
  emisor_nombre: string | null
  emisor_rif: string | null
  cliente_nombre: string | null
  cliente_rif: string | null
  cliente_id: number | null
  proveedor_id: number | null
  documento: string | null
  bl: string | null
  fecha: string | null
  renglones_usd: RenglonUsd[]
  pagos: Pago[]
  pagado: string | null
  cuadre: Cuadre | null
  bloqueos: string[]
  avisos: string[]
  dudas: string[]
  anotaciones: string[]
  confianza: string
  modelo: string
}

const ORIGEN: Record<string, string> = {
  documento: "impresa en el documento",
  tecleada: "escrita a mano aquí",
  pago: "sacada de lo pagado",
}

/**
 * Leer con IA la preliquidación (o factura, o pedido) y sus transferencias.
 *
 * ⚠ **Lo leído se VUELCA al formulario y ahí se revisa y se guarda**: este panel no
 * guarda nada. Es la misma regla que las facturas —nada leído por una IA se asienta
 * solo— con un camino más corto, porque un comprobante provisional no mueve dinero.
 *
 * ⚠ **Cambiar la tasa NO vuelve a leer.** Los renglones en dólares ya están leídos y la
 * conversión es aritmética: se pide a `POST /cuadrar`, que no sale a Google ni gasta
 * cuota. Releer daría otra lectura de lo mismo.
 *
 * ⚠ **El Ctrl+V se escucha en el DOCUMENTO, no en el panel.** `AdjuntosNuevos` deja el
 * pegado a quien lo usa, y aquí no lo había: la zona decía «pega con Ctrl+V» y no pasaba
 * nada. Un `onPaste` en el panel tampoco bastaría: la zona punteada no toma el foco, así
 * que tras pulsarla el pegado llega al `body` y nunca sube por el panel. Va a la zona donde
 * está el foco o la última que se pulsó; si no, al documento de cobro mientras falte y
 * después a los comprobantes. Sólo se corta el pegado si trae archivos: pegar texto en el
 * contexto o en la tasa sigue siendo pegar texto.
 */
export function LectorRetencion({
  porcentajes,
  onVolcar,
}: {
  porcentajes: Pick<
    BorradorRetencion,
    "alicuota_iva" | "porcentaje_ret_iva" | "porcentaje_ret_islr" | "sustraendo_islr"
  >
  onVolcar: (cambios: Partial<BorradorRetencion>) => void
}) {
  const [documentos, setDocumentos] = useState<Adjuntando[]>([])
  const [pagos, setPagos] = useState<Adjuntando[]>([])
  const [rechazados, setRechazados] = useState<string[]>([])
  const [contexto, setContexto] = useState("")
  const [tasa, setTasa] = useState("")
  const [recuadre, setRecuadre] = useState<Cuadre | null>(null)
  const zonaDocumentos = useRef<HTMLDivElement>(null)
  const zonaPagos = useRef<HTMLDivElement>(null)
  const ultimaZona = useRef<"documentos" | "pagos" | null>(null)

  function recibirDocumentos(archivos: File[]) {
    const r = sumar(documentos, archivos)
    setDocumentos(r.adjuntos)
    setRechazados(r.rechazados)
  }

  function recibirPagos(archivos: File[]) {
    const r = sumar(pagos, archivos)
    setPagos(r.adjuntos)
    setRechazados(r.rechazados)
  }

  useEffect(() => {
    function alPegar(evento: ClipboardEvent) {
      const archivos = archivosPegados(evento.clipboardData, documentos.length + pagos.length)
      if (archivos.length === 0) return
      const donde = evento.target instanceof Node ? evento.target : null
      const zona =
        donde && zonaPagos.current?.contains(donde)
          ? "pagos"
          : donde && zonaDocumentos.current?.contains(donde)
            ? "documentos"
            : (ultimaZona.current ?? (documentos.length === 0 ? "documentos" : "pagos"))
      evento.preventDefault()
      if (zona === "pagos") recibirPagos(archivos)
      else recibirDocumentos(archivos)
    }
    document.addEventListener("paste", alPegar)
    return () => document.removeEventListener("paste", alPegar)
  })

  const leer = useMutation({
    mutationFn: () => {
      const datos = new FormData()
      for (const d of documentos) datos.append("documentos", d.archivo)
      for (const p of pagos) datos.append("pagos", p.archivo)
      datos.append("contexto", contexto)
      datos.append("tasa", tasa.trim() ? aDecimal(tasa) : "")
      datos.append("alicuota_iva", aDecimal(porcentajes.alicuota_iva))
      datos.append("porcentaje_ret_iva", aDecimal(porcentajes.porcentaje_ret_iva))
      datos.append("porcentaje_ret_islr", aDecimal(porcentajes.porcentaje_ret_islr))
      return api.post<Lectura>("/api/v1/retenciones-provisionales/leer", datos)
    },
    onSuccess: () => setRecuadre(null),
  })

  const cuadrar = useMutation({
    mutationFn: (l: Lectura) =>
      api.post<Cuadre>("/api/v1/retenciones-provisionales/cuadrar", {
        renglones_usd: l.renglones_usd,
        tasa: tasa.trim() ? aDecimal(tasa) : null,
        pagado: l.pagado,
        alicuota_iva: aDecimal(porcentajes.alicuota_iva),
        porcentaje_ret_iva: aDecimal(porcentajes.porcentaje_ret_iva),
        porcentaje_ret_islr: aDecimal(porcentajes.porcentaje_ret_islr),
        sustraendo_islr: aDecimal(porcentajes.sustraendo_islr || "0"),
      }),
    onSuccess: setRecuadre,
  })

  const lectura = leer.data
  const cuadre = recuadre ?? lectura?.cuadre ?? null

  function volcar(l: Lectura, c: Cuadre) {
    const pagosOk = l.pagos.filter((p) => p.monto && p.estado !== "RECHAZADA")
    onVolcar({
      agente_nombre: l.cliente_nombre ?? "",
      agente_rif: l.cliente_rif ?? "",
      cliente_id: l.cliente_id,
      beneficiario_nombre: l.emisor_nombre ?? "",
      beneficiario_rif: l.emisor_rif ?? "",
      proveedor_id: l.proveedor_id,
      documento: l.documento ?? "",
      bl: l.bl ?? "",
      ...(l.fecha ? { fecha: l.fecha } : {}),
      tasa_cambio: c.tasa ?? "",
      lineas: conDolares(c, l.renglones_usd),
      observaciones: pagosOk.length
        ? `Pagado: ${pagosOk
            .map(
              (p) =>
                `${monto(p.monto ?? "0")} Bs${p.referencia ? ` ref. ${p.referencia}` : ""}${p.fecha ? ` (${comoFecha(p.fecha)})` : ""}`,
            )
            .join("; ")}.`
        : "",
    })
  }

  return (
    <fieldset className="superficie grid gap-3 p-4">
      <legend className="rotulo flex items-center gap-1.5 px-1">
        <Sparkles size={15} aria-hidden={true} />
        Leer con IA
      </legend>
      <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
        La preliquidación, pedido o factura, y si los tienes, los comprobantes de pago. Los archivos
        se envían a Google (Gemini) para leerlos; lo leído se vuelca al formulario para revisarlo
        antes de guardar.
      </p>

      {/* `onPointerDown` y no `onClick`: recuerda la zona aunque el clic caiga en el
          texto o el borde punteado, que no son controles. */}
      <div
        ref={zonaDocumentos}
        className="grid gap-1"
        onPointerDown={() => {
          ultimaZona.current = "documentos"
        }}
      >
        <span className="text-xs font-medium">Documento de cobro</span>
        <AdjuntosNuevos
          adjuntos={documentos}
          rechazados={[]}
          etiqueta="Adjuntar preliquidación o factura"
          onArchivos={recibirDocumentos}
          onQuitar={(id) => setDocumentos(documentos.filter((d) => d.id !== id))}
        />
      </div>
      <div
        ref={zonaPagos}
        className="grid gap-1"
        onPointerDown={() => {
          ultimaZona.current = "pagos"
        }}
      >
        <span className="text-xs font-medium">Comprobantes de pago (opcional)</span>
        <AdjuntosNuevos
          adjuntos={pagos}
          rechazados={rechazados}
          etiqueta="Adjuntar transferencias"
          onArchivos={recibirPagos}
          onQuitar={(id) => setPagos(pagos.filter((p) => p.id !== id))}
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-[1fr_10rem]">
        <label className="grid gap-1">
          <span className="text-xs font-medium">Contexto (opcional)</span>
          <input
            className="campo h-9 text-sm"
            placeholder="Ej.: el ISLR va sobre todos los servicios"
            value={contexto}
            maxLength={300}
            onChange={(e) => setContexto(e.target.value)}
          />
        </label>
        <label className="grid gap-1">
          <span className="text-xs font-medium">Tasa Bs/USD (opcional)</span>
          <input
            className="campo tabular-nums h-9 text-sm"
            inputMode="decimal"
            placeholder="Del pago, si falta"
            value={tasa}
            onChange={(e) => setTasa(e.target.value)}
          />
        </label>
      </div>
      <Boton
        variante="primario"
        className="justify-self-start"
        disabled={documentos.length === 0 || leer.isPending}
        onClick={() => leer.mutate()}
      >
        <Sparkles size={15} aria-hidden={true} />
        {leer.isPending ? "Leyendo… (10–30 s)" : "Leer"}
      </Boton>
      {leer.error && (
        <p className="text-sm" role="alert" style={{ color: "var(--destructive)" }}>
          {leer.error.message}
        </p>
      )}

      {lectura && (
        <div className="grid gap-2 border-t pt-3 text-sm" style={{ borderColor: "var(--border)" }}>
          <p>
            <strong>{lectura.emisor_nombre ?? "¿Emisor?"}</strong> cobra a{" "}
            <strong>{lectura.cliente_nombre ?? "¿cliente?"}</strong>
            {lectura.documento ? ` · documento ${lectura.documento}` : ""}
            {lectura.bl ? ` · B/L ${lectura.bl}` : ""}
          </p>
          {lectura.pagos.length > 0 && (
            <ul className="tabular-nums grid gap-0.5 text-xs">
              {lectura.pagos.map((p, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: los pagos leídos no tienen identidad
                <li key={i}>
                  Pago {i + 1}: {p.monto ? monto(p.monto) : "¿monto?"} {p.moneda ?? ""}
                  {p.referencia ? ` · ref. ${p.referencia}` : ""}
                  {p.fecha ? ` · ${comoFecha(p.fecha)}` : ""} · {p.estado.toLowerCase()}
                </li>
              ))}
            </ul>
          )}
          {cuadre && (
            <div className="tabular-nums grid gap-0.5 text-xs">
              <span>
                {cuadre.tasa
                  ? `Tasa ${cuadre.tasa.replace(/0+$/, "").replace(/\.$/, "")} ${ORIGEN[cuadre.origen_tasa ?? ""] ?? ""}`
                  : "Montos en bolívares impresos en el documento"}
              </span>
              <span>
                Total {monto(cuadre.calculo.total)} · retenido{" "}
                {monto(cuadre.calculo.total_retenido)} · a pagar{" "}
                <strong>{monto(cuadre.calculo.neto)}</strong>
              </span>
              {lectura.pagado && (
                <span
                  style={{
                    color: esCero(cuadre.diferencia) ? "var(--success)" : "var(--warning)",
                  }}
                >
                  Pagado {monto(lectura.pagado)}
                  {esCero(cuadre.diferencia)
                    ? " · cuadra al céntimo"
                    : ` · diferencia ${monto(cuadre.diferencia ?? "0")}`}
                </span>
              )}
            </div>
          )}
          {[
            ...lectura.bloqueos.map((t) => ["bloqueo", t]),
            ...lectura.avisos.map((t) => ["aviso", t]),
          ].map(([clase, texto]) => (
            <p
              key={`${clase}-${texto}`}
              className="text-xs"
              role={clase === "bloqueo" ? "alert" : undefined}
              style={{ color: clase === "bloqueo" ? "var(--destructive)" : "var(--warning)" }}
            >
              {texto}
            </p>
          ))}
          {[...lectura.dudas, ...lectura.anotaciones.map((a) => `Anotado a mano: ${a}`)].map(
            (t) => (
              <p key={t} className="text-xs" style={{ color: "var(--fg-muted)" }}>
                {t}
              </p>
            ),
          )}
          <div className="flex flex-wrap gap-2">
            {lectura.renglones_usd.length > 0 && (
              <Boton disabled={cuadrar.isPending} onClick={() => cuadrar.mutate(lectura)}>
                <RefreshCw size={15} aria-hidden={true} />
                Recalcular con la tasa
              </Boton>
            )}
            {cuadre && (
              <Boton variante="primario" onClick={() => volcar(lectura, cuadre)}>
                <ArrowDownToLine size={15} aria-hidden={true} />
                Volcar al formulario
              </Boton>
            )}
          </div>
          {cuadrar.error && (
            <p className="text-xs" role="alert" style={{ color: "var(--destructive)" }}>
              {cuadrar.error.message}
            </p>
          )}
          <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
            Leído por {lectura.modelo || "Gemini"} · confianza {lectura.confianza.toLowerCase()}
          </p>
        </div>
      )}
    </fieldset>
  )
}

/** «0.00», «-0.00» o «0»: cuadra. Sin pasar por `Number`, como todo monto (regla 18). */
function esCero(texto: string | null): boolean {
  return texto !== null && /^-?0+(\.0+)?$/.test(texto.trim())
}

/**
 * Las líneas del cuadre con sus dólares, cuando se convirtieron desde dólares.
 *
 * ⚠ Se guardan para poder reajustar después a la tasa del pago desde el documento y
 * no desde unos bolívares ya redondeados. Si los bolívares los imprimía el documento
 * (`tasa` nula), no hay conversión que conservar.
 */
function conDolares(c: Cuadre, renglones: RenglonUsd[]): LineaRetencion[] {
  const usd = c.tasa !== null && renglones.length === c.lineas.length
  return c.lineas.map((ln, i) => ({
    ...ln,
    monto_usd: usd ? (renglones[i]?.monto_usd ?? "") : "",
  }))
}
